<template>
  <section class="life-style-form pb-3">

    <div class="d-flex justify-content-between flex-wrap align-items-center">
      <h6 class="text-evollu-gray-light is-bold py-3 mb-0">
          {{$t('clinical.people.anamnesis.life_style')}}
      </h6>

      <div class="d-flex align-items-center justify-content-end">

        <div v-if="hasUnsavedChanges">
          <small class="text-orange is-bold">
            {{$t('clinical.people.anamnesis.unsaved')}}
          </small>
        </div>

        <div v-else-if="value && value.updated_at !== null && value.updated_at !== undefined">
          <small>
            {{$t('clinical.people.anamnesis.updated_at')}}
          </small>
          <small class="is-bold">
            {{$t('clinical.people.anamnesis.updated_by', {date: getExpressiveDate(value.updated_at), name: value.updated_by ? value.updated_by.name : ''})}}
          </small>
        </div>

        <i
          v-if="collapsable"
          class="clickable p-3"
          :class="{'fa fa-chevron-up': !isCollapsed, 'fa fa-chevron-down': isCollapsed}"
          @click="isCollapsed = !isCollapsed"
        >
        </i>
      </div>
    </div>
    <div v-if="!isCollapsed" class="mt-3">

      <slot name="beforeForm"></slot>

      <form class="evollu-form" v-if="value">

        <!-- Hearing Difficulty Relation -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.asks_for_repeat')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.commonOptions"
              :value="value.asks_to_repeat"
              @input="v => updateValue('asks_to_repeat', v)"
              :key="index"
              :option="option.id"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('asks_to_repeat')">
            <small>
              {{$t(formErrors.get('asks_to_repeat'))}}
            </small>
          </div>
        </div>

        <!-- Hearing Difficulty Environments -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.hearing_difficulties_environments')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <multiselect
              :value="formOptions.hearingDifficultyEnvironments.filter(o => value.hearing_difficulty_environment_ids.some(id => id === o.id))"
              :options="formOptions.hearingDifficultyEnvironments"
              @input="val => updateValue('hearing_difficulty_environment_ids', val ? val.map(v => v.id) : null)"
              track-by="id"
              :customLabel="option => $t(option.name)"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('clinical.people.anamnesis.hearing_difficulties_environments')"
              :allow-empty="true"
              :multiple="true"
              :searchable="true"
              :close-on-select="false"
              open-direction="bottom"
              :disabled="!editing"
              >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              </multiselect>
            </div>

          <div class="text-danger col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('hearing_difficulty_environment_ids')">
            <small>
              {{$t(formErrors.get('hearing_difficulty_environment_ids'))}}
            </small>
          </div>
        </div>

        <!-- Hearing Difficulty Environments Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.hearing_difficulties_environments_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="hearing-difficulties-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.hearing_difficulties_environments_obs')"
              :value="value.hearing_difficulty_environments_obs"
              @input="v => updateValue('hearing_difficulty_environments_obs', v)"
              :error="formErrors.has('hearing_difficulty_environments_obs')"
              :error-messages="[$t(formErrors.get('hearing_difficulty_environments_obs'))]"
              :disabled="!editing"
            />
          </div>

        </div>

        <!-- Isolation Because of Hearing Loss -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.isolation_because_of_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.commonOptions"
              :value="value.isolation_because_of_hearing_loss"
              @input="v => updateValue('isolation_because_of_hearing_loss', v)"
              :key="index"
              :option="option.id"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('isolation_because_of_hearing_loss')">
            <small>
              {{$t(formErrors.get('isolation_because_of_hearing_loss'))}}
            </small>
          </div>
        </div>

        <!-- Shame Because of Hearing Loss -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.shame_because_of_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.commonOptions"
              :value="value.shame_because_of_hearing_loss"
              @input="v => updateValue('shame_because_of_hearing_loss', v)"
              :key="index"
              :option="option.id"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('shame_because_of_hearing_loss')">
            <small>
              {{$t(formErrors.get('shame_because_of_hearing_loss'))}}
            </small>
          </div>
        </div>

        <!-- Nervous Because of Hearing Loss -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.nervous_because_of_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.commonOptions"
              :value="value.nervous_because_of_hearing_loss"
              @input="v => updateValue('nervous_because_of_hearing_loss', v)"
              :key="index"
              :option="option.id"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('nervous_because_of_hearing_loss')">
            <small>
              {{$t(formErrors.get('nervous_because_of_hearing_loss'))}}
            </small>
          </div>
        </div>

      </form>

      <div v-else class="ml-2">
        <slot name="noData">
          <span>{{$t('clinical.people.no_data')}}</span>
        </slot>
      </div>
    </div>

  </section>
</template>

<script>
import Errors from '@/utils/StandardErrors'
import Multiselect from 'vue-multiselect'
import * as AnamnesisOptions from './AnamnesisOptions'

export default {
  props: {
    value: {
      type: Object,
      required: false
    },
    formErrors: {
      type: Object,
      required: false,
      default: () => {
        return new Errors()
      }
    },
    editing: {
      type: Boolean,
      default: true
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Multiselect
  },
  data: function () {
    return {
      isCollapsed: this.collapsable && this.collapsed,
      formOptions: AnamnesisOptions,
      hasUnsavedChanges: false
    }
  },
  methods: {
    getHasUnsavedChanges: function () {
      return this.hasUnsavedChanges
    },
    setHasUnsavedChanges: function (value) {
      this.hasUnsavedChanges = value
    },
    getDefaultValue: function () {
      return {
        asks_to_repeat: null,
        hearing_difficulty_environment_ids: [],
        hearing_difficulty_environments_obs: null,
        isolation_because_of_hearing_loss: null,
        shame_because_of_hearing_loss: null,
        nervous_because_of_hearing_loss: null,
      }
    },
    updateValue: function (field, value) {
      if (this.formErrors.has(field)) {
        this.formErrors.clear(field)
      }
      this.$emit('input', { ...this.value, [field]: value })
      this.$emit('clearErrors', field)

      if (!this.hasUnsavedChanges) {
        this.hasUnsavedChanges = true
      }
    },
    getExpressiveDate: function (value) {
      if (value) {
        if (this.$options.filters.isToday(value)) {
          return this.$t('date_helpers.today')
        } else {
          return this.$options.filters.localTime(value, `DD [${this.$t('date_helpers.day_of_month_separator')}] MMMM [${this.$t('date_helpers.day_of_month_separator')}] YYYY`, '', this.locale)
        }
      }
      return ''
    }
  },
  watch: {
    value: function (value) {
      if ((value === null || value === undefined) && this.editing) {
        this.$emit('input', { ...this.getDefaultValue() })
      }
    },
    collapsed: function () {
      this.isCollapsed = this.collapsable && this.collapsed
    },
    editing: {
      handler: function (editing) {
        if (editing) {
          this.$emit('input', { ...this.getDefaultValue(), ...this.value })
        }
      },
      immediate: true
    }
  }
}
</script>
